import {
  type ClientMsg,
  ClientMsgKind,
  type ClientMsgOf,
  ErrorCode,
  HostChangeReason,
  Phase,
  type Player,
  PlayerStatus,
  Presence,
  Role,
  type Room,
  type ServerMsg,
  ServerMsgKind,
} from '../schemas';
import type { Rng } from './rng';
import {
  assignRoles,
  computeWinner as computeUndercoverWinner,
  shuffle,
  tallyVotes,
  validVoteTargets as undercoverValidVoteTargets,
} from './rules';
import { pickWordPair } from './words';

/** Player-count limits for Undercover. Below MIN: not enough hidden roles. Above MAX: presence list overflows. */
export const MIN_PLAYERS = 3;
export const MAX_PLAYERS = 25;

/**
 * Static feature flags consumed by the web UI to drive labels, screens,
 * and i18n branches. Replaces the old per-mode `manifest.features` lookup.
 */
export const INFILTRE_FEATURES = {
  hasClochard: true,
  extraPhases: [Phase.ClochardGuess] as const,
  minPlayers: MIN_PLAYERS,
  maxPlayers: MAX_PLAYERS,
} as const;

/** Side-effect deps injected by host. Keeps `reduce` deterministic for `(state, msg, ctx)`. */
export interface EngineCtx {
  now: number;
  rng: Rng;
  authorize: (actorPlayerId: string) => boolean;
}

export interface ReduceResult {
  state: Room;
  emitted: readonly ServerMsg[];
}

/** Engine reads time/randomness only through `ctx` — never `Date.now` or `Math.random`. */
export const reduce = (state: Room, msg: ClientMsg, ctx: EngineCtx): ReduceResult => {
  if (!ctx.authorize(msg.actorPlayerId)) return fail(state, ErrorCode.Unauthorized);

  const raw = dispatch(state, msg, ctx);
  if (raw.state === state) return raw;
  return stampActivity(raw, ctx.now);
};

/**
 * Pick next host when current disconnects. Ranks by presence (online > reconnecting,
 * offline excluded), then status (alive > eliminated), then `Player.order`. Returns
 * `null` if no candidate — caller decides delete vs leave intact.
 */
export const selectNextHost = (room: Room): string | null => {
  const presenceRank: Record<Presence, number> = {
    [Presence.Online]: 0,
    [Presence.Reconnecting]: 1,
    [Presence.Offline]: 3,
  };
  const statusRank = (p: Player): number => (p.status === PlayerStatus.Alive ? 0 : 1);
  const candidates = room.players
    .filter((p) => !p.isHost)
    .filter((p) => (p.presence ?? Presence.Online) !== Presence.Offline)
    .map((p) => ({
      p,
      score: presenceRank[p.presence ?? Presence.Online] * 10 + statusRank(p),
    }))
    .sort((a, b) => a.score - b.score || a.p.order - b.p.order);
  return candidates[0]?.p.id ?? null;
};

/** Roles + word pair for a fresh round. Pure given `(state, rng)`. */
const prepareUndercoverRound = (
  state: Room,
  rng: Rng,
): { players: Player[]; flicWord: string; infiltreWord: string } => {
  const roles = assignRoles(
    state.players,
    state.options.includeClochard,
    rng,
    state.options.infiltreCount,
  );
  const [flicWord, infiltreWord] = pickWordPair(
    state.options.locale,
    state.options.categories,
    () => rng.next(),
  );
  const players: Player[] = state.players.map((p, i) => {
    const role = roles[i]!;
    const word = role === Role.Flic ? flicWord : role === Role.Infiltre ? infiltreWord : undefined;
    const next: Player = { ...p, role, status: PlayerStatus.Alive, order: i };
    if (word !== undefined) next.word = word;
    return next;
  });
  return { players, flicWord, infiltreWord };
};

const dispatch = (state: Room, msg: ClientMsg, ctx: EngineCtx): ReduceResult => {
  switch (msg.kind) {
    case ClientMsgKind.CreateRoom:
      return fail(state, ErrorCode.RoomAlreadyExists);
    case ClientMsgKind.JoinRoom:
      return handleJoin(state, msg, ctx);
    case ClientMsgKind.LeaveRoom:
      return handleLeave(state, msg);
    case ClientMsgKind.StartGame:
      return handleStart(state, msg, ctx);
    case ClientMsgKind.AcknowledgeWord:
      return handleAcknowledge(state, msg, ctx);
    case ClientMsgKind.OpenVote:
      return handleOpenVote(state, msg);
    case ClientMsgKind.CastVote:
      return handleVote(state, msg, ctx);
    case ClientMsgKind.ClochardGuess:
      return handleClochardGuess(state, msg);
    case ClientMsgKind.NextRound:
      return handleNextRound(state, msg, ctx);
    case ClientMsgKind.PromoteHost:
      return handlePromoteHost(state, msg);
    case ClientMsgKind.EliminatePlayer:
      return handleEliminatePlayer(state, msg);
    case ClientMsgKind.RequestNextRound:
      return handleRequestNextRound(state, msg, ctx);
    case ClientMsgKind.UpdateRoomOptions:
      return handleUpdateRoomOptions(state, msg);
    case ClientMsgKind.UpdateMugshot:
      return handleUpdateMugshot(state, msg);
    case ClientMsgKind.UpdateName:
      return handleUpdateName(state, msg);
    case ClientMsgKind.Pong:
      // Handled by broadcaster heartbeat tracker; no-op here for exhaustiveness.
      return same(state);
    case ClientMsgKind.MarkPresence:
      return handleMarkPresence(state, msg);
    case ClientMsgKind.Reaction:
      return handleReaction(state, msg, ctx);
  }
};

/** Stamp `lastActivityAt` (and `finishedAt` on phase boundary) and rewrite emitted snapshots. */
const stampActivity = (result: ReduceResult, now: number): ReduceResult => {
  const stamped: Room = { ...result.state, lastActivityAt: now };
  if (stamped.phase === Phase.Finished && stamped.finishedAt === undefined) {
    stamped.finishedAt = now;
  }
  const emitted = result.emitted.map(
    (m): ServerMsg => (m.kind === ServerMsgKind.RoomState ? { ...m, room: stamped } : m),
  );
  return { state: stamped, emitted };
};

const handleJoin = (state: Room, msg: ClientMsgOf<'JoinRoom'>, ctx: EngineCtx): ReduceResult => {
  if (state.phase !== Phase.Lobby) return fail(state, ErrorCode.WrongPhase, 'game already started');
  if (state.players.some((p) => p.id === msg.actorPlayerId)) return same(state);
  if (state.players.length >= 25) return fail(state, ErrorCode.RoomFull);

  const player: Player = {
    id: msg.actorPlayerId,
    name: msg.name,
    status: PlayerStatus.Alive,
    isHost: state.players.length === 0,
    order: state.players.length,
    presence: Presence.Online,
    lastSeenAt: ctx.now,
    ...(msg.mugshot ? { mugshot: msg.mugshot } : {}),
  };
  const next: Room = { ...state, players: [...state.players, player] };
  return same(next, [
    { kind: ServerMsgKind.PlayerJoined, player },
    { kind: ServerMsgKind.RoomState, room: next },
  ]);
};

const handleLeave = (state: Room, msg: ClientMsgOf<'LeaveRoom'>): ReduceResult => {
  if (!state.players.some((p) => p.id === msg.actorPlayerId)) return same(state);
  const next: Room = {
    ...state,
    players: state.players.filter((p) => p.id !== msg.actorPlayerId),
  };
  return same(next, [
    { kind: ServerMsgKind.PlayerLeft, playerId: msg.actorPlayerId },
    { kind: ServerMsgKind.RoomState, room: next },
  ]);
};

/** Re-deal roles + reset round state. Shared by first-time `StartGame` and rematch `NextRound`. */
const dealNewGame = (state: Room, ctx: EngineCtx): ReduceResult => {
  if (state.players.length < MIN_PLAYERS) {
    return fail(state, ErrorCode.NotEnoughPlayers, `need at least ${MIN_PLAYERS} players`);
  }
  if (state.players.length > MAX_PLAYERS) {
    return fail(state, ErrorCode.TooManyPlayers, `at most ${MAX_PLAYERS} players`);
  }

  const prepared = prepareUndercoverRound(state, ctx.rng);
  const players: Player[] = [...prepared.players];
  // Clochard cannot speak first — swap with next non-Clochard.
  const ordered = state.options.randomizeSpeakingOrder ? shuffle(players, ctx.rng) : [...players];
  if (ordered[0]?.role === Role.Clochard) {
    const swapIdx = ordered.findIndex((p, i) => i > 0 && p.role !== Role.Clochard);
    if (swapIdx > 0) {
      [ordered[0], ordered[swapIdx]] = [ordered[swapIdx]!, ordered[0]!];
    }
  }
  const speakingOrder = ordered.map((p) => p.id);
  const { flicWord, infiltreWord } = prepared;

  // Drop `finishedAt` — stampActivity only sets it, never clears it.
  const { finishedAt: _drop, ...rest } = state;
  const next: Room = {
    ...(rest as Room),
    phase: Phase.Reveal,
    players,
    flicWord,
    infiltreWord,
    speakingOrder,
    currentSpeaker: undefined,
    votes: [],
    eliminatedThisRound: undefined,
    winner: undefined,
    nextRoundRequests: [],
    revealAcknowledgedBy: [],
    round: 1,
  };
  return same(next, [
    { kind: ServerMsgKind.GameStarted, flicWord, infiltreWord },
    ...players.map(
      (p): ServerMsg => ({
        kind: ServerMsgKind.WordAssigned,
        playerId: p.id,
        role: p.role!,
        word: p.word ?? '',
      }),
    ),
    { kind: ServerMsgKind.PhaseChanged, phase: Phase.Reveal },
    { kind: ServerMsgKind.RoomState, room: next },
  ]);
};

const handleStart = (state: Room, msg: ClientMsgOf<'StartGame'>, ctx: EngineCtx): ReduceResult => {
  const host = state.players.find((p) => p.id === msg.actorPlayerId);
  if (!host?.isHost) return fail(state, ErrorCode.OnlyHost, 'only host can start');
  if (state.phase !== Phase.Lobby) return fail(state, ErrorCode.WrongPhase, 'already started');
  return dealNewGame(state, ctx);
};

const handleAcknowledge = (
  state: Room,
  msg: ClientMsgOf<'AcknowledgeWord'>,
  ctx: EngineCtx,
): ReduceResult => {
  if (state.phase !== Phase.Reveal) return same(state);
  if (!state.players.some((p) => p.id === msg.actorPlayerId)) {
    return fail(state, ErrorCode.NotAMember);
  }

  const alreadyAcked = state.revealAcknowledgedBy.includes(msg.actorPlayerId);
  const revealAcknowledgedBy = alreadyAcked
    ? state.revealAcknowledgedBy
    : [...state.revealAcknowledgedBy, msg.actorPlayerId];

  const aliveIds = state.players.filter((p) => p.status === PlayerStatus.Alive).map((p) => p.id);
  const allAcked = aliveIds.every((id) => revealAcknowledgedBy.includes(id));

  if (!allAcked) {
    if (alreadyAcked) return same(state);
    const next: Room = { ...state, revealAcknowledgedBy };
    return same(next, [{ kind: ServerMsgKind.RoomState, room: next }]);
  }

  const next: Room = {
    ...enterDiscuss(state, ctx),
    phase: Phase.Discuss,
    revealAcknowledgedBy: [],
  };
  return same(next, [
    { kind: ServerMsgKind.PhaseChanged, phase: Phase.Discuss },
    { kind: ServerMsgKind.RoomState, room: next },
  ]);
};

/** Set `discussDeadlineAt` for server auto-OpenVote timer. `discussionSeconds=0` disables. */
const enterDiscuss = (state: Room, ctx: EngineCtx): Room => {
  const seconds = state.options.discussionSeconds;
  if (seconds <= 0) {
    const { discussDeadlineAt: _drop, ...rest } = state;
    return rest as Room;
  }
  return { ...state, discussDeadlineAt: ctx.now + seconds * 1000 };
};

/** Drop `discussDeadlineAt` via rebuild (not undefined) to keep TypeBox schema stable. */
const exitDiscuss = (state: Room): Room => {
  const { discussDeadlineAt: _drop, ...rest } = state;
  return rest as Room;
};

const handleOpenVote = (state: Room, msg: ClientMsgOf<'OpenVote'>): ReduceResult => {
  if (state.phase !== Phase.Discuss) return fail(state, ErrorCode.WrongPhase);
  const host = state.players.find((p) => p.id === msg.actorPlayerId);
  if (!host?.isHost) return fail(state, ErrorCode.OnlyHost, 'only host can open vote');

  const next: Room = { ...exitDiscuss(state), phase: Phase.Vote, votes: [] };
  return same(next, [
    { kind: ServerMsgKind.PhaseChanged, phase: Phase.Vote },
    { kind: ServerMsgKind.RoomState, room: next },
  ]);
};

const handleVote = (state: Room, msg: ClientMsgOf<'CastVote'>, ctx: EngineCtx): ReduceResult => {
  if (state.phase !== Phase.Vote) return fail(state, ErrorCode.WrongPhase, 'not in vote phase');
  const targets = undercoverValidVoteTargets(state, msg.actorPlayerId);
  if (!targets.some((t) => t.id === msg.targetPlayerId)) {
    return fail(state, ErrorCode.InvalidTarget, 'invalid vote target');
  }

  const votes = [
    ...state.votes.filter((v) => v.voter !== msg.actorPlayerId),
    { voter: msg.actorPlayerId, target: msg.targetPlayerId },
  ];
  const livingIds = state.players.filter((p) => p.status === PlayerStatus.Alive).map((p) => p.id);
  const allVoted = livingIds.every((id) => votes.some((v) => v.voter === id));

  if (!allVoted) {
    const next: Room = { ...state, votes };
    return same(next, [
      { kind: ServerMsgKind.VoteCast, voter: msg.actorPlayerId, target: msg.targetPlayerId },
      { kind: ServerMsgKind.RoomState, room: next },
    ]);
  }

  return resolveVotes(state, votes, ctx);
};

const resolveVotes = (state: Room, votes: Room['votes'], ctx: EngineCtx): ReduceResult => {
  const tally = tallyVotes(votes);
  if (!tally.topId || tally.tied) {
    // Tie loops back to discuss; re-arm timer on new deadline.
    const next: Room = { ...enterDiscuss(state, ctx), votes: [], phase: Phase.Discuss };
    return same(next, [
      { kind: ServerMsgKind.PhaseChanged, phase: Phase.Discuss },
      { kind: ServerMsgKind.RoomState, room: next },
    ]);
  }

  const eliminated = state.players.find((p) => p.id === tally.topId);
  if (!eliminated) return fail(state, ErrorCode.Internal, 'eliminated player not found');

  // Clochard elimination triggers a guess phase before finalization.
  if (eliminated.role === Role.Clochard) {
    const next: Room = {
      ...state,
      votes,
      phase: Phase.ClochardGuess,
      eliminatedThisRound: tally.topId,
    };
    return same(next, [
      { kind: ServerMsgKind.PhaseChanged, phase: Phase.ClochardGuess },
      { kind: ServerMsgKind.RoomState, room: next },
    ]);
  }

  return finalizeElimination(state, votes, tally.topId);
};

const finalizeElimination = (
  state: Room,
  votes: Room['votes'],
  eliminatedId: string,
): ReduceResult => {
  const players = state.players.map((p) =>
    p.id === eliminatedId ? { ...p, status: PlayerStatus.Eliminated } : p,
  );
  const post: Room = {
    ...state,
    players,
    votes,
    eliminatedThisRound: eliminatedId,
    phase: Phase.RevealVote,
  };
  const eliminated = players.find((p) => p.id === eliminatedId);
  if (!eliminated?.role) throw new Error('engine: eliminated player has no role');
  const role = eliminated.role;
  const winner = computeUndercoverWinner(post);

  const emitted: ServerMsg[] = [
    { kind: ServerMsgKind.PlayerEliminated, playerId: eliminatedId, role },
    { kind: ServerMsgKind.PhaseChanged, phase: Phase.RevealVote },
  ];

  if (winner) {
    const finished: Room = { ...post, phase: Phase.Finished, winner };
    emitted.push(
      {
        kind: ServerMsgKind.GameEnded,
        winner,
        flicWord: state.flicWord ?? '',
        infiltreWord: state.infiltreWord ?? '',
      },
      { kind: ServerMsgKind.PhaseChanged, phase: Phase.Finished },
      { kind: ServerMsgKind.RoomState, room: finished },
    );
    return { state: finished, emitted };
  }

  emitted.push({ kind: ServerMsgKind.RoomState, room: post });
  return { state: post, emitted };
};

const handleClochardGuess = (state: Room, msg: ClientMsgOf<'ClochardGuess'>): ReduceResult => {
  if (state.phase !== Phase.ClochardGuess) return fail(state, ErrorCode.WrongPhase);
  if (state.eliminatedThisRound !== msg.actorPlayerId) return fail(state, ErrorCode.NotYourGuess);
  const target = state.flicWord?.toLowerCase().trim();
  const guess = msg.guess.toLowerCase().trim();
  if (target && guess === target) {
    const finished: Room = { ...state, phase: Phase.Finished, winner: Role.Clochard };
    return same(finished, [
      {
        kind: ServerMsgKind.GameEnded,
        winner: Role.Clochard,
        flicWord: state.flicWord ?? '',
        infiltreWord: state.infiltreWord ?? '',
      },
      { kind: ServerMsgKind.PhaseChanged, phase: Phase.Finished },
      { kind: ServerMsgKind.RoomState, room: finished },
    ]);
  }
  return finalizeElimination(state, state.votes, msg.actorPlayerId);
};

const handleNextRound = (
  state: Room,
  msg: ClientMsgOf<'NextRound'>,
  ctx: EngineCtx,
): ReduceResult => {
  // `Finished → Reveal` = rematch (re-deal). `RevealVote → Discuss` = normal advance.
  const isRematch = state.phase === Phase.Finished;
  if (state.phase !== Phase.RevealVote && !isRematch) return fail(state, ErrorCode.WrongPhase);
  const host = state.players.find((p) => p.id === msg.actorPlayerId);
  if (!host?.isHost) return fail(state, ErrorCode.OnlyHost, 'only host can advance');

  if (isRematch) return dealNewGame(state, ctx);

  const next: Room = {
    ...enterDiscuss(state, ctx),
    phase: Phase.Discuss,
    round: state.round + 1,
    votes: [],
    nextRoundRequests: [],
    eliminatedThisRound: undefined,
  };
  return same(next, [
    { kind: ServerMsgKind.PhaseChanged, phase: Phase.Discuss },
    { kind: ServerMsgKind.RoomState, room: next },
  ]);
};

/** Actor must be current host OR no host (engine auto-rotation). Target must be alive. */
const handlePromoteHost = (state: Room, msg: ClientMsgOf<'PromoteHost'>): ReduceResult => {
  if (state.phase === Phase.Finished) return fail(state, ErrorCode.WrongPhase);
  const currentHost = state.players.find((p) => p.isHost);
  const isCurrentHost = currentHost?.id === msg.actorPlayerId;
  if (!isCurrentHost && currentHost !== undefined) {
    return fail(state, ErrorCode.Unauthorized);
  }
  const target = state.players.find((p) => p.id === msg.newHostId);
  if (!target) return fail(state, ErrorCode.InvalidTarget);
  if (target.status !== PlayerStatus.Alive) return fail(state, ErrorCode.InvalidTarget);
  if (target.isHost) return same(state);

  const previousHostId = currentHost?.id;
  const players: Player[] = state.players.map((p) => {
    if (p.id === msg.newHostId) return { ...p, isHost: true };
    if (p.isHost) return { ...p, isHost: false };
    return p;
  });
  const next: Room = { ...state, players };
  return same(next, [
    {
      kind: ServerMsgKind.HostChanged,
      newHostId: msg.newHostId,
      ...(previousHostId !== undefined ? { previousHostId } : {}),
      reason: msg.reason ?? HostChangeReason.Manual,
    },
    { kind: ServerMsgKind.RoomState, room: next },
  ]);
};

/** Standalone fast-path: host taps voted-out player. Synthesizes unanimous ballot then reuses finalize. */
const handleEliminatePlayer = (state: Room, msg: ClientMsgOf<'EliminatePlayer'>): ReduceResult => {
  const allowedPhase = state.phase === Phase.Discuss || state.phase === Phase.Vote;
  if (!allowedPhase) return fail(state, ErrorCode.WrongPhase);
  const host = state.players.find((p) => p.id === msg.actorPlayerId);
  if (!host?.isHost) return fail(state, ErrorCode.OnlyHost, 'only_host_can_eliminate');
  const target = state.players.find((p) => p.id === msg.targetPlayerId);
  if (!target || target.status !== PlayerStatus.Alive) return fail(state, ErrorCode.InvalidTarget);

  const aliveVoters = state.players.filter((p) => p.status === PlayerStatus.Alive);
  const syntheticVotes: Room['votes'] = aliveVoters.map((v) => ({
    voter: v.id,
    target: msg.targetPlayerId,
  }));

  // Standalone skips Vote entirely — drop deadline if still in Discuss.
  const base = state.phase === Phase.Discuss ? exitDiscuss(state) : state;

  // Clochard elimination triggers a guess phase before finalization.
  if (target.role === Role.Clochard) {
    const next: Room = {
      ...base,
      votes: syntheticVotes,
      phase: Phase.ClochardGuess,
      eliminatedThisRound: target.id,
    };
    return same(next, [
      { kind: ServerMsgKind.PhaseChanged, phase: Phase.ClochardGuess },
      { kind: ServerMsgKind.RoomState, room: next },
    ]);
  }
  return finalizeElimination(base, syntheticVotes, target.id);
};

/**
 * Replay-round vote. Any seated player opts in. Majority in `RevealVote` auto-advances.
 * In `Finished` count accumulates but never auto-advances — host triggers rematch manually.
 */
const handleRequestNextRound = (
  state: Room,
  msg: ClientMsgOf<'RequestNextRound'>,
  ctx: EngineCtx,
): ReduceResult => {
  if (state.phase !== Phase.RevealVote && state.phase !== Phase.Finished) {
    return fail(state, ErrorCode.WrongPhase);
  }
  if (!state.players.some((p) => p.id === msg.actorPlayerId)) {
    return fail(state, ErrorCode.NotAMember);
  }
  const requests = state.nextRoundRequests.includes(msg.actorPlayerId)
    ? state.nextRoundRequests
    : [...state.nextRoundRequests, msg.actorPlayerId];
  const needed = Math.ceil(state.players.length / 2);
  const requested: Room = { ...state, nextRoundRequests: requests };
  const requestedMsg: ServerMsg = {
    kind: ServerMsgKind.NextRoundRequested,
    playerId: msg.actorPlayerId,
    total: requests.length,
    needed,
  };

  if (state.phase === Phase.RevealVote && requests.length >= needed) {
    const advanced: Room = {
      ...enterDiscuss(requested, ctx),
      phase: Phase.Discuss,
      round: requested.round + 1,
      votes: [],
      nextRoundRequests: [],
      eliminatedThisRound: undefined,
    };
    return same(advanced, [
      requestedMsg,
      { kind: ServerMsgKind.PhaseChanged, phase: Phase.Discuss },
      { kind: ServerMsgKind.RoomState, room: advanced },
    ]);
  }
  return same(requested, [requestedMsg, { kind: ServerMsgKind.RoomState, room: requested }]);
};

/** Host-only options edit. Lobby phase only — options become immutable once round starts. */
const handleUpdateRoomOptions = (
  state: Room,
  msg: ClientMsgOf<'UpdateRoomOptions'>,
): ReduceResult => {
  if (state.phase !== Phase.Lobby) return fail(state, ErrorCode.WrongPhase);
  const host = state.players.find((p) => p.id === msg.actorPlayerId);
  if (!host?.isHost) return fail(state, ErrorCode.OnlyHost, 'only_host_can_update_options');
  const patch = msg.options;
  const next: Room = {
    ...state,
    options: {
      ...state.options,
      ...(patch.includeClochard !== undefined ? { includeClochard: patch.includeClochard } : {}),
      ...(patch.locale !== undefined ? { locale: patch.locale } : {}),
      ...(patch.categories !== undefined ? { categories: [...patch.categories] } : {}),
      ...(patch.discussionSeconds !== undefined
        ? { discussionSeconds: patch.discussionSeconds }
        : {}),
      ...(patch.infiltreCount !== undefined ? { infiltreCount: patch.infiltreCount } : {}),
      ...(patch.hideCode !== undefined ? { hideCode: patch.hideCode } : {}),
    },
  };
  return same(next, [{ kind: ServerMsgKind.RoomState, room: next }]);
};

const handleUpdateMugshot = (state: Room, msg: ClientMsgOf<'UpdateMugshot'>): ReduceResult => {
  const idx = state.players.findIndex((p) => p.id === msg.actorPlayerId);
  if (idx < 0) return fail(state, ErrorCode.UnknownPlayer);
  const next: Room = {
    ...state,
    players: state.players.map((p, i) => (i === idx ? { ...p, mugshot: msg.mugshot } : p)),
  };
  return same(next, [{ kind: ServerMsgKind.RoomState, room: next }]);
};

const handleUpdateName = (state: Room, msg: ClientMsgOf<'UpdateName'>): ReduceResult => {
  const idx = state.players.findIndex((p) => p.id === msg.actorPlayerId);
  if (idx < 0) return fail(state, ErrorCode.UnknownPlayer);
  const trimmed = msg.name.trim();
  if (trimmed.length === 0) return fail(state, ErrorCode.NameRequired);
  const next: Room = {
    ...state,
    players: state.players.map((p, i) => (i === idx ? { ...p, name: trimmed } : p)),
  };
  return same(next, [{ kind: ServerMsgKind.RoomState, room: next }]);
};

/** System-driven presence flip. Emits delta-only `PresenceChanged` (no full RoomState). */
const handleMarkPresence = (state: Room, msg: ClientMsgOf<'MarkPresence'>): ReduceResult => {
  const idx = state.players.findIndex((p) => p.id === msg.actorPlayerId);
  if (idx < 0) return same(state);
  const current = state.players[idx]!;
  if (
    (current.presence ?? Presence.Online) === msg.presence &&
    current.lastSeenAt === msg.lastSeenAt
  ) {
    return same(state);
  }
  const players = state.players.map((p, i) =>
    i === idx ? { ...p, presence: msg.presence, lastSeenAt: msg.lastSeenAt } : p,
  );
  const next: Room = { ...state, players };
  return same(next, [
    {
      kind: ServerMsgKind.PresenceChanged,
      playerId: msg.actorPlayerId,
      presence: msg.presence,
      lastSeenAt: msg.lastSeenAt,
    },
  ]);
};

/**
 * Ephemeral emoji reaction. Engine never mutates state — reactions are a
 * purely visual effect. The reducer just emits a `Reaction` ServerMsg so
 * the broadcaster fans it out to every WS in the room. We stamp `ctx.now`
 * so clients can dedup and animate consistently.
 */
const handleReaction = (
  state: Room,
  msg: ClientMsgOf<'Reaction'>,
  ctx: EngineCtx,
): ReduceResult => {
  if (!state.players.some((p) => p.id === msg.actorPlayerId)) return same(state);
  return same(state, [
    {
      kind: ServerMsgKind.Reaction,
      playerId: msg.actorPlayerId,
      emoji: msg.emoji,
      at: ctx.now,
    },
  ]);
};

const fail = (state: Room, code: ErrorCode, message?: string): ReduceResult => ({
  state,
  emitted: [
    {
      kind: ServerMsgKind.Error,
      code,
      phase: state.phase,
      ...(message ? { message } : {}),
    },
  ],
});

const same = (state: Room, emitted: readonly ServerMsg[] = []): ReduceResult => ({
  state,
  emitted,
});
